// File: app/src/utils/substituteExercises.js
// Equipment mismatch detection and AI-driven exercise substitution for Co-Train Live Mode.
// Pure utility functions — no React dependencies.

import { cloneDeep } from "lodash";

/**
 * Walk the plan tree and find leaf exercises whose `equipment` field
 * is not available in the participant's local equipment list.
 *
 * @param {Object} planData   – The host's workout plan tree (root node)
 * @param {string[]} localEquipment – The participant's available equipment array
 * @returns {Array<{ nodeId, path, label, equipment, muscleGroups, movementPattern }>}
 */
export function detectMismatches(planData, localEquipment) {
  if (!planData || !localEquipment) return [];

  const localSet = new Set(localEquipment.map((e) => e.toLowerCase()));
  // "Bodyweight" / null exercises never mismatch
  localSet.add("bodyweight");

  const mismatches = [];

  const walk = (node, currentPath) => {
    if (!node) return;

    // Leaf node with equipment requirement
    if (node.equipment && !localSet.has(node.equipment.toLowerCase())) {
      mismatches.push({
        nodeId: node.id,
        path: [...currentPath],
        label: node.label,
        equipment: node.equipment,
        muscleGroups: node.muscleGroups || [],
        movementPattern: node.movementPattern || "",
      });
    }

    // Recurse into children
    if (Array.isArray(node.children)) {
      node.children.forEach((child, idx) => {
        walk(child, [...currentPath, "children", idx]);
      });
    }
  };

  walk(planData, []);
  return mismatches;
}

/**
 * Build a focused LLM prompt requesting substitutions for mismatched exercises.
 * Designed to return a small, parseable JSON array.
 *
 * @param {Array} mismatches    – Output from detectMismatches()
 * @param {string[]} localEquipment – Participant's available equipment
 * @returns {string} The prompt string
 */
export function buildSubstitutionPrompt(mismatches, localEquipment) {
  const exerciseList = mismatches
    .map(
      (m, i) =>
        `${i + 1}. "${m.label}" (requires: ${m.equipment}, targets: ${m.muscleGroups.join(", ") || "general"}, pattern: ${m.movementPattern || "unspecified"})`
    )
    .join("\n");

  return `You are an expert fitness coach. A participant has joined a group workout but doesn't have all the equipment.

## Available Equipment
${localEquipment.join(", ")}

## Exercises Needing Substitution
${exerciseList}

## Task
For each exercise above, provide a substitute that:
1. Uses ONLY the participant's available equipment (or bodyweight)
2. Targets the SAME muscle groups
3. Matches the movement pattern as closely as possible
4. Maintains similar difficulty/intensity

## Output Format
Respond with ONLY a JSON array. Each element must have these exact fields:
[
  {
    "nodeId": "original exercise's nodeId (string)",
    "label": "Substitute Exercise Name",
    "equipment": "Equipment used (or null for bodyweight)",
    "description": "Brief (1 sentence) description of the substitute"
  }
]

Respond with ONLY the JSON array, no other text.`;
}

/**
 * Deep-clone the plan tree and swap in substituted exercises by nodeId.
 *
 * @param {Object} planData          – The original plan tree
 * @param {Array} substitutions      – Array of { nodeId, label, equipment, description }
 * @returns {Object} A new plan tree with substituted exercises, plus a summary of changes
 */
export function applySubstitutions(planData, substitutions) {
  if (!planData || !substitutions?.length) return { plan: planData, changes: [] };

  const subMap = new Map(substitutions.map((s) => [s.nodeId, s]));
  const plan = cloneDeep(planData);
  const changes = [];

  const walk = (node) => {
    if (!node) return;
    if (subMap.has(node.id)) {
      const sub = subMap.get(node.id);
      const originalLabel = node.label;
      node.label = sub.label;
      node.equipment = sub.equipment || null;
      if (sub.description) node.description = sub.description;
      // Mark as substituted for UI display
      node._substituted = true;
      node._originalLabel = originalLabel;
      changes.push({
        nodeId: node.id,
        from: originalLabel,
        to: sub.label,
        equipment: sub.equipment,
      });
    }
    if (Array.isArray(node.children)) {
      node.children.forEach(walk);
    }
  };

  walk(plan);
  return { plan, changes };
}
